import { Fragment, useEffect, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { PublicVehicleOpportunityForm } from "./PublicVehicleOpportunityForm";
import { PublicFinancingPanel } from "./PublicFinancing";
import { PublicNewsletter, type PublicNewsletterSettings } from "./PublicNewsletter";
import "./stand-newsletter.css";
import "./public-template-tokens.css";

export type TemplatePage = { slug: string; title: string; status?: string };
export type TemplateService = { key:string; title:string; description:string; icon:string; classification:"operational"|"promotional"; destination_type:"none"|"contact"|"page"|"url"; destination?:string; active?:boolean; sort_order?:number };
export type TemplateMetric = {key:string;label:string;value:number;format:"number"|"years"|"months"|"rating";source:"calculated"|"declared"|"mixed";source_label:string;updated_at?:string|null;sort_order?:number};
export type TemplateCredit = {public_financing_enabled:boolean;registration_number?:string;category?:string};
export type TemplateFinancing = {public_financing_enabled:boolean;simulator_enabled:boolean;minimum_term_months:number;maximum_term_months:number;term_step_months:number;minimum_down_payment_percent:number;nominal_annual_rate?:number|null;annual_percentage_rate?:number|null;headline:string;introduction:string;disclosure:string;registration_number?:string;category?:string};
export type TemplateInstitutionalMedia={primary_kind?:"image"|"video";exterior_url?:string|null;mobile_image_url?:string|null;video_url?:string|null;video_cover_url?:string|null;exterior_alt?:string;video_alt?:string;desktop_position?:string;mobile_position?:string;gallery?:{url:string;alt?:string;position?:string}[]};
export type TemplateTestimonial={id:string;name:string;locality?:string;text:string;rating:number;source:string;source_label?:string;source_url?:string;photo_url?:string|null;authorized:boolean;published:boolean;sort_order:number};
export type TemplateReputation={google_profile_url?:string|null;google_review_url?:string|null;average_rating?:number|null;review_count?:number|null;rating_source?:string|null;rating_source_label?:string|null;testimonials?:TemplateTestimonial[]};
export type BranchHour = {
  weekday: number;
  enabled: boolean;
  opens_at?: string | null;
  closes_at?: string | null;
  lunch_enabled?: boolean;
  lunch_starts_at?: string | null;
  lunch_ends_at?: string | null;
};
export type TemplateBranch = {
  id: string;
  name: string;
  is_headquarters: boolean;
  email?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
  address_line?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country_code?: string | null;
  public_notes?: string | null;
  hours?: BranchHour[];
};
export type TemplateStand = {
  slug: string;
  name: string;
  legalName?: string | null;
  taxId?: string | null;
  website?: string | null;
  city?: string | null;
  email?: string | null;
  phone?: string | null;
  identityName?: string;
  tagline?: string;
  headline?: string;
  intro?: string;
  button?: string;
  logo?: string | null;
  pages?: TemplatePage[];
  branches?: TemplateBranch[];
  profile?: {
    slogan?: string;
    founded_year?: number | null;
    signature?: string;
    short_description?: string;
    story?: string;
    mission?: string;
    values?: string[];
    differentiators?: string[];
    showroom_image_url?: string | null;
    direct_phone?: string;
    social_links?: Record<string, string>;
  };
  services?: TemplateService[];
  metrics?: TemplateMetric[];
  credit?: TemplateCredit;
  financing?: TemplateFinancing;
  media?: TemplateInstitutionalMedia;
  reputation?: TemplateReputation;
  newsletter?: PublicNewsletterSettings;
};
export type PublicTemplateProps = {
  layout: string;
  stand: TemplateStand;
  vehicleCount: number;
  vehicleCards: ReactNode;
  heroMedia?: ReactNode;
  catalogControls?: ReactNode;
  catalogCategories?: ReactNode;
  visitorTools?: ReactNode;
  preview?: boolean;
};
type Props = PublicTemplateProps;
const previewHero=(p:Props)=>p.heroMedia??(p.preview?<ResponsiveHeroMedia desktop="/demo-volvo-xc40.png" alt="Viatura demonstrativa do template"/>:undefined);

const pageLinks = (stand: TemplateStand) => [
  { label: "Stock", href: "#stock" },
  ...(stand.financing?.public_financing_enabled?[{label:"Financiamento",href:`/stand/${stand.slug}/financiamento`}]:[]),
  ...((stand.services ?? []).some((service) => service.active !== false)
    ? [{ label: "Serviços", href: "#servicos" }]
    : []),
  ...(stand.pages ?? [])
    .filter((p) => (p.status ?? "published") === "published")
    .map((p) => ({ label: p.title, href: `/stand/${stand.slug}/p/${p.slug}` })),
  { label: "Contactos", href: "#contactos" },
];
const address = (b?: TemplateBranch) =>
  b
    ? [
        b.address_line,
        [b.postal_code, b.city].filter(Boolean).join(" "),
        b.country_code,
      ]
        .filter(Boolean)
        .join(", ")
    : "";
const mapUrl = (b?: TemplateBranch) =>
  `https://www.google.com/maps?q=${encodeURIComponent(address(b))}&output=embed`;
const mapLink = (b?: TemplateBranch) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address(b))}`;
const weekdays = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const socialLabels: Record<string, string> = { facebook:"Facebook", instagram:"Instagram", youtube:"YouTube", linkedin:"LinkedIn", tiktok:"TikTok", other:"Outra ligação" };

function InstitutionalProfile({ stand }: { stand: TemplateStand }) {
  const profile=stand.profile;
  const hasCopy=Boolean(profile?.short_description||profile?.story||profile?.mission||profile?.values?.length||profile?.differentiators?.length||profile?.founded_year);
  if(!hasCopy&&!profile?.showroom_image_url)return null;
  return <section className={`theme-institutional${profile?.showroom_image_url?" has-image":" without-image"}`}><div className="theme-institutional-copy"><p>O STAND</p><h2>{profile?.signature||`Conheça ${stand.identityName||stand.name}`}</h2>{profile?.short_description&&<strong>{profile.short_description}</strong>}{profile?.story&&<p className="theme-institutional-story">{profile.story}</p>}<div className="theme-institutional-facts">{profile?.founded_year&&<span><b>Desde {profile.founded_year}</b>Experiência construída ao longo do tempo.</span>}{profile?.mission&&<span><b>Missão</b>{profile.mission}</span>}{profile?.values?.length?<span><b>Valores</b>{profile.values.join(" · ")}</span>:null}</div>{profile?.differentiators?.length?<ul>{profile.differentiators.map(item=><li key={item}>{item}</li>)}</ul>:null}</div>{profile?.showroom_image_url&&<img src={profile.showroom_image_url} alt={`Espaço de ${stand.identityName||stand.name}`} loading="lazy"/>}</section>;
}
function InstitutionalMediaBlock({stand}:{stand:TemplateStand}){
 const media=stand.media;if(!media)return null;const gallery=media.gallery??[];if(!media.exterior_url&&!media.video_url&&!gallery.length)return null;
 const desktop=media.desktop_position??"center",mobile=media.mobile_position??desktop;
 const showVideo=Boolean(media.video_url&&(media.primary_kind==="video"||!media.exterior_url));
 return <section className="theme-institutional-media" aria-label="Espaço e apresentação do stand" style={{"--media-desktop-position":desktop,"--media-mobile-position":mobile} as CSSProperties}>
  {showVideo?<figure className="theme-institutional-video"><video controls preload="metadata" poster={media.video_cover_url??undefined} aria-label={media.video_alt||"Vídeo institucional do stand"}><source src={media.video_url!}/></video>{media.video_alt&&<figcaption>{media.video_alt}</figcaption>}</figure>:media.exterior_url?<picture className="theme-institutional-main"><source media="(max-width: 600px)" srcSet={media.mobile_image_url||media.exterior_url}/><img src={media.exterior_url} alt={media.exterior_alt||`Espaço de ${stand.identityName||stand.name}`} loading="lazy"/></picture>:media.video_url?<figure className="theme-institutional-video"><video controls preload="metadata" poster={media.video_cover_url??undefined} aria-label={media.video_alt||"Vídeo institucional do stand"}><source src={media.video_url}/></video></figure>:null}
  {gallery.length>0&&<div className="theme-institutional-gallery">{gallery.map((item,index)=><img key={`${item.url}-${index}`} src={item.url} alt={item.alt||`Espaço de ${stand.identityName||stand.name}`} loading="lazy" style={{objectPosition:item.position??"center"}}/>)}</div>}
 </section>;
}

export function ThemeBrand({
  stand,
  compact = false,
}: {
  stand: TemplateStand;
  compact?: boolean;
}) {
  return (
    <span className={`theme-brand${compact ? " compact" : ""}`}>
      {stand.logo ? (
        <img src={stand.logo} alt="" />
      ) : (
        <b>{(stand.identityName || stand.name).slice(0, 1)}</b>
      )}
      <span>
        <strong>{stand.identityName || stand.name}</strong>
        {!compact && (
          <small>
            {stand.tagline ||
              stand.profile?.signature ||
              stand.city ||
              "Automóveis"}
          </small>
        )}
      </span>
    </span>
  );
}
export function ThemeNav({ stand, rooted=false }: { stand: TemplateStand; rooted?:boolean }) {
  const links = pageLinks(stand).map(link=>rooted&&link.href.startsWith("#")?{...link,href:`/stand/${stand.slug}${link.href}`}:link);
  return (
    <div className="theme-nav-shell">
      <nav className="theme-nav" aria-label="Navegação principal">
        {links.map((x) => <a key={x.href} href={x.href}>{x.label}</a>)}
      </nav>
      <details className="theme-mobile-nav" onKeyDown={event=>{if(event.key==="Escape"){event.currentTarget.removeAttribute("open");event.currentTarget.querySelector("summary")?.focus();}}}>
        <summary aria-label="Abrir navegação">Menu</summary>
        <nav aria-label="Navegação principal no telemóvel">
          {links.map((x) => <a key={x.href} href={x.href} onClick={event=>event.currentTarget.closest("details")?.removeAttribute("open")}>{x.label}</a>)}
        </nav>
      </details>
    </div>
  );
}
export function ResponsiveHeroMedia({kind="image",desktop,mobile,fallback,alt=""}:{kind?:"image"|"video";desktop?:string|null;mobile?:string|null;fallback?:string|null;alt?:string}) {
  const [reduceMotion,setReduceMotion]=useState(()=>typeof window!=="undefined"&&window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  useEffect(()=>{
    const query=window.matchMedia("(prefers-reduced-motion: reduce)");
    const update=()=>setReduceMotion(query.matches);
    update();query.addEventListener("change",update);
    return ()=>query.removeEventListener("change",update);
  },[]);
  if(!desktop)return null;
  return kind==="video"
    ? <><video className={`stand-public-hero-media${fallback?" has-reduced-motion-poster":""}`} autoPlay={!reduceMotion} controls={reduceMotion&&!fallback} muted loop playsInline preload="metadata" poster={fallback??undefined} aria-label={alt||"Vídeo de apresentação do stand"} src={desktop}/>{fallback&&<picture className="public-reduced-motion-poster" aria-hidden="true"><img src={fallback} alt="" fetchPriority="high"/></picture>}</>
    : <picture><source media="(max-width: 600px)" srcSet={mobile||desktop}/><img className="stand-public-hero-media" src={desktop} alt={alt} fetchPriority="high"/></picture>;
}
export function Hero({
  stand,
  media,
  kicker,
}: {
  stand: TemplateStand;
  media?: ReactNode;
  kicker: string;
}) {
  return (
    <>
      {media}
      <div className="theme-hero-copy">
        <p>{stand.tagline || kicker}</p>
        <h1>{stand.headline || "A sua próxima viatura começa aqui"}</h1>
        <span>
          {stand.intro ||
            "Uma seleção cuidada, acompanhamento próximo e toda a informação para escolher com confiança."}
        </span>
        <div>
          <a className="theme-primary" href="#stock">
            {stand.button || "Ver viaturas"}
          </a>
          <a className="theme-secondary" href="#contactos">
            Falar connosco
          </a>
        </div>
      </div>
    </>
  );
}
export function Stock({
  count,
  cards,
  controls,
  cardSize = "compact",
  label = "Disponíveis agora",
}: {
  count: number;
  cards: ReactNode;
  controls?: ReactNode;
  cardSize?: "compact" | "large" | "mosaic";
  label?: string;
}) {
  return (
    <section className="theme-stock" id="stock">
      <header>
        <div>
          <p>STOCK ATUAL</p>
          <h2>{label}</h2>
        </div>
        <span>
          {count} {count === 1 ? "viatura" : "viaturas"}
        </span>
      </header>
      {controls}
      <div className={`stand-vehicle-grid card-size-${cardSize} vehicle-count-${count===0?"empty":count===1?"one":count===2?"two":"many"} vehicle-count-value-${Math.min(Math.max(count,0),4)}`}>{cards}</div>
    </section>
  );
}
const serviceIcons:Record<string,string>={tools:"⌁",shield:"◇",exchange:"⇄",vehicle:"▱",delivery:"→",insurance:"○",support:"+",inspection:"✓",document:"≡",star:"★"};
const safeTemplateHref=(value?:string|null)=>{if(!value)return undefined;try{const url=new URL(value,typeof window!=="undefined"?window.location.origin:"https://invalid.local");return ["http:","https:","mailto:","tel:"].includes(url.protocol)?value:undefined;}catch{return undefined;}};
const serviceHref=(stand:TemplateStand,item:TemplateService)=>item.destination_type==="contact"?"#contactos":item.destination_type==="page"?`/stand/${stand.slug}/p/${(item.destination||"").replace(/^\/+/,"")}`:item.destination_type==="url"?safeTemplateHref(item.destination):undefined;
export function Services({stand}:{stand:TemplateStand}) {
 const conversionKeys=new Set(["vehicle_purchase","trade_in","vehicle_listing"]);
 const items=(stand.services??[]).filter(x=>x.active!==false&&!conversionKeys.has(x.key));
 if(!items.length)return null;
 return <section className="theme-trust" id="servicos" aria-label="Serviços do stand">{items.map((item,index)=>{const content=<><b aria-hidden="true">{serviceIcons[item.icon]||String(index+1).padStart(2,"0")}</b><h3>{item.title}</h3><p>{item.description}</p>{item.classification==="promotional"&&<small>Informação promocional</small>}</>;const href=serviceHref(stand,item);return <article key={item.key}>{href?<a href={href} target={item.destination_type==="url"?"_blank":undefined} rel={item.destination_type==="url"?"noopener noreferrer":undefined}>{content}</a>:content}</article>})}</section>;
}
export function Metrics({stand}:{stand:TemplateStand}){
 const items=stand.metrics??[];
 const credit=stand.financing?.public_financing_enabled?stand.financing:null;
 if(!items.length&&!credit)return null;
 const display=(item:TemplateMetric)=>item.format==="rating"?`${new Intl.NumberFormat("pt-PT",{maximumFractionDigits:1}).format(item.value)}/5`:item.format==="years"?`${item.value} ${item.value===1?"ano":"anos"}`:item.format==="months"?`${item.value} ${item.value===1?"mês":"meses"}`:new Intl.NumberFormat("pt-PT").format(item.value);
 return <><section className="theme-metrics" aria-label="Indicadores do stand">{items.map(item=><article key={item.key}><strong>{display(item)}</strong><span>{item.label}</span><small>{item.source_label}</small></article>)}</section>{credit&&<aside className="theme-credit-disclosure"><strong>Intermediário de crédito</strong><span>{credit.category}</span><small>Registo/licença {credit.registration_number}</small></aside>}</>;
}
export function Reputation({stand}:{stand:TemplateStand}){
 const reputation=stand.reputation,testimonials=(reputation?.testimonials??[]).filter(item=>item.authorized&&item.published),hasRating=reputation?.average_rating!=null&&reputation?.review_count!=null;
 if(!hasRating&&!testimonials.length&&!reputation?.google_profile_url&&!reputation?.google_review_url)return null;
 return <section className="theme-reputation" aria-label="Avaliações e testemunhos"><header><div><p>O QUE DIZEM SOBRE NÓS</p><h2>A experiência dos nossos clientes</h2></div>{hasRating&&<div className="theme-reputation-summary"><strong>{new Intl.NumberFormat("pt-PT",{maximumFractionDigits:1}).format(Number(reputation!.average_rating))}/5</strong><span aria-label={`${reputation!.average_rating} em 5 estrelas`}>{"★".repeat(Math.round(Number(reputation!.average_rating)))}</span><small>{new Intl.NumberFormat("pt-PT").format(Number(reputation!.review_count))} opiniões · {reputation?.rating_source_label}</small></div>}</header>{testimonials.length>0&&<div className="theme-testimonials">{testimonials.map(item=><article key={item.id}>{item.photo_url?<img src={item.photo_url} alt="" loading="lazy"/>:<b aria-hidden="true">{item.name.slice(0,1).toUpperCase()}</b>}<div><span aria-label={`${item.rating} em 5 estrelas`}>{"★".repeat(item.rating)}</span><blockquote>“{item.text}”</blockquote><strong>{item.name}</strong>{item.locality&&<small>{item.locality}</small>}{item.source_url?<a href={item.source_url} target="_blank" rel="noopener noreferrer">{item.source_label||"Ver origem"} ↗</a>:item.source_label&&<small>{item.source_label}</small>}</div></article>)}</div>}<footer>{reputation?.google_profile_url&&<a href={reputation.google_profile_url} target="_blank" rel="noopener noreferrer">Ver perfil público ↗</a>}{reputation?.google_review_url&&<a className="theme-primary" href={reputation.google_review_url} target="_blank" rel="noopener noreferrer">Deixar uma avaliação</a>}</footer></section>;
}
export function Sell({ stand }: { stand: TemplateStand }) {
  const services=(stand.services??[]).filter(item=>["vehicle_purchase","trade_in","vehicle_listing"].includes(item.key)&&item.active!==false);
  if(!services.length)return null;
  const service=services[0];
  return (
    <section className="theme-sell" id="vender">
      <div>
        <p>QUER VENDER O SEU CARRO?</p>
        <h2>{service.title}</h2>
        <span>{service.description}</span>
      </div>
      <PublicVehicleOpportunityForm slug={stand.slug} services={services} />
    </section>
  );
}
export function Faq({stand}:{stand:TemplateStand}) {
  const trade=stand.services?.find(item=>item.key==="trade_in"&&item.active!==false);
  return (
    <section className="theme-faq">
      <header>
        <p>PERGUNTAS FREQUENTES</p>
        <h2>Antes de avançar</h2>
      </header>
      <div>
        <details>
          <summary>Posso marcar uma visita ou test drive?</summary>
          <p>
            Sim. Contacte o stand para confirmar a disponibilidade da viatura e
            o melhor horário.
          </p>
        </details>
        {trade&&<details>
          <summary>Aceitam retomas?</summary>
          <p>{trade.description}</p>
        </details>}
      </div>
    </section>
  );
}
export function BranchDetails({
  branch,
  stand,
}: {
  branch: TemplateBranch;
  stand: TemplateStand;
}) {
  const branchAddress = address(branch);
  const phone = branch.phone || (branch.is_headquarters ? stand.phone : null);
  const email = branch.email || (branch.is_headquarters ? stand.email : null);
  return (
    <article
      className={`theme-branch-details ${branch.is_headquarters ? "is-headquarters" : "is-branch"}`}
    >
      <header>
        <span>{branch.is_headquarters ? "Sede" : "Filial"}</span>
        <h3>
          {branchAddress ? (
            <a
              href={mapLink(branch)}
              target="_blank"
              rel="noreferrer"
              title={`Abrir ${branch.name} na navegação`}
            >
              {branch.name} ↗
            </a>
          ) : (
            branch.name
          )}
        </h3>
      </header>
      {branchAddress && <address>{branchAddress}</address>}
      {branch.public_notes && (
        <p className="theme-branch-notes">{branch.public_notes}</p>
      )}
      <div className="theme-contact-links">
        {phone && <a href={`tel:${phone}`}>{phone}</a>}
        {branch.whatsapp && (
          <a href={`https://wa.me/${branch.whatsapp.replace(/\D/g, "")}`}>
            WhatsApp
          </a>
        )}
        {email && <a href={`mailto:${email}`}>{email}</a>}
        {branchAddress && (
          <a href={mapLink(branch)} target="_blank" rel="noreferrer">
            Abrir navegação ↗
          </a>
        )}
      </div>
      {Boolean(branch.hours?.length) && (
        <div className="theme-hours">
          {branch.hours
            ?.filter((h) => h.enabled)
            .map((h) => (
              <span key={h.weekday}>
                <b>{weekdays[h.weekday - 1]}</b> {h.opens_at?.slice(0, 5)}–
                {h.closes_at?.slice(0, 5)}
              </span>
            ))}
        </div>
      )}
    </article>
  );
}
export function ThemeFooter({ stand }: { stand: TemplateStand }) {
  const branches = stand.branches ?? [];
  const hq = branches.find((b) => b.is_headquarters) || branches[0];
  const otherBranches = branches.filter((branch) => branch.id !== hq?.id);
  const legal = stand.legalName || stand.name;
  return (
    <>
      <InstitutionalProfile stand={stand} />
      <InstitutionalMediaBlock stand={stand} />
      <PublicNewsletter slug={stand.slug} settings={stand.newsletter} />
      <footer className="theme-footer" id="contactos">
        <div className="theme-footer-main">
          <ThemeBrand stand={stand} />
          <div>
            <h3>Explorar</h3>
            {pageLinks(stand).map((x) => (
              <a key={x.href} href={x.href}>
                {x.label}
              </a>
            ))}
          </div>
          <div>
            <h3>Informação legal</h3>
            <ThemeLegalLinks slug={stand.slug}/>
          </div>
          <div>
            <h3>Contactos</h3>
            {(hq?.phone || stand.phone) && <a href={`tel:${hq?.phone || stand.phone}`}>{hq?.phone || stand.phone}</a>}
            {(hq?.email || stand.email) && <a href={`mailto:${hq?.email || stand.email}`}>{hq?.email || stand.email}</a>}
            {(hq && address(hq)) ? <a href={mapLink(hq)} target="_blank" rel="noreferrer">{address(hq)} ↗</a> : stand.city && <span>{stand.city}</span>}
            {otherBranches.map((branch) => <span key={branch.id}>{branch.name}{branch.city ? ` · ${branch.city}` : ""}</span>)}
          </div>
          <div>
            <h3>Empresa</h3>
            <span>{legal}</span>
            {stand.taxId && <span>NIF {stand.taxId}</span>}
            {stand.website && (
              <a href={stand.website}>
                {stand.website.replace(/^https?:\/\//, "")}
              </a>
            )}
            {stand.profile?.direct_phone && <a href={`tel:${stand.profile.direct_phone}`}>{stand.profile.direct_phone}</a>}
            {Object.entries(stand.profile?.social_links ?? {}).filter(([,url])=>Boolean(url)).map(([network,url])=><a key={network} href={url} target="_blank" rel="noreferrer">{socialLabels[network] ?? network} ↗</a>)}
          </div>
        </div>
        <div className="theme-footer-bottom">
          <small>
            © {new Date().getFullYear()} {legal}. Todos os direitos reservados.
          </small>
          <small>Site gerido com Vorno Stand</small>
        </div>
      </footer>
    </>
  );
}

function ReferenceCommercialFlow({stand}:{stand:TemplateStand}) {
  return <>
    <Services stand={stand}/>
    <div className="theme-promo-grid">
      <Sell stand={stand}/>
      <PublicFinancingPanel slug={stand.slug} financing={stand.financing}/>
    </div>
    <Reputation stand={stand}/>
  </>;
}

export function ThemeLegalLinks({slug}:{slug:string}) {
  return <>
    <a href={`/stand/${slug}/legal/privacidade`}>Política de Privacidade</a>
    <a href={`/stand/${slug}/legal/cookies`}>Política de Cookies</a>
    <a href={`/stand/${slug}/legal/termos`}>Termos e Condições</a>
    <a href="https://www.livroreclamacoes.pt/Inicio/" target="_blank" rel="noreferrer">Livro de Reclamações ↗</a>
  </>;
}

function Avenida(p: Props) {
  const media=previewHero(p);
  return (
    <>
      <header className="avenida-header">
        <a href={`/stand/${p.stand.slug}`}>
          <ThemeBrand stand={p.stand} />
        </a>
        <ThemeNav stand={p.stand} />
        <div className="theme-header-actions"><a href="#contactos">Contactar</a>{p.visitorTools}</div>
      </header>
      <main>
        <section className="avenida-hero">
          <Hero
            stand={p.stand}
            media={media}
            kicker="O seu próximo automóvel"
          />
          <div className="avenida-search">
            <div className="avenida-search-heading">
              <span>Encontre a sua próxima viatura</span>
              <a href="#stock">Ver resultados →</a>
            </div>
            {p.catalogControls ?? <p>{p.vehicleCount} {p.vehicleCount===1?"viatura disponível":"viaturas disponíveis"}</p>}
          </div>
        </section>
        {p.catalogCategories}
        <Stock count={p.vehicleCount} cards={p.vehicleCards} cardSize="compact" label="Destaques" />
        <Metrics stand={p.stand}/>
        <ReferenceCommercialFlow stand={p.stand}/>
      </main>
      <ThemeFooter stand={p.stand} />
    </>
  );
}
function Atelier(p: Props) {
  const media=previewHero(p);
  const founded=p.stand.profile?.founded_year;
  const institutional=[
    p.stand.phone?{label:p.stand.phone,href:`tel:${p.stand.phone}`}:null,
    p.stand.email?{label:p.stand.email,href:`mailto:${p.stand.email}`}:null,
    founded?{label:`Desde ${founded}`}:null,
    p.stand.city?{label:p.stand.city}:null,
  ].filter((item):item is {label:string;href?:string}=>Boolean(item));
  return (
    <>
      <div className="atelier-note" aria-label="Informação institucional">
        {institutional.length?institutional.map((item,index)=><Fragment key={`${item.label}-${index}`}>{index>0&&<i aria-hidden="true">·</i>}{item.href?<a href={item.href}>{item.label}</a>:<span>{item.label}</span>}</Fragment>):<span>Automóveis escolhidos com critério</span>}
      </div>
      <header className="atelier-header">
        <ThemeNav stand={p.stand} />
        <a href={`/stand/${p.stand.slug}`}>
          <ThemeBrand stand={p.stand} />
        </a>
        <div className="theme-header-actions"><a href="#contactos">Marcar visita</a>{p.visitorTools}</div>
      </header>
      <main>
        <section className={`atelier-hero ${media?"has-media":"without-media"}`}>
          <div className="atelier-copy">
            <Hero stand={p.stand} kicker="Coleção automóvel" />
            <div className="atelier-search">
              <strong>Procure a sua próxima viatura</strong>
              {p.catalogControls ?? <span>{p.vehicleCount} {p.vehicleCount===1?"viatura disponível":"viaturas disponíveis"}</span>}
            </div>
          </div>
          {media&&<div className="atelier-media">
            {media}
            <span>Edição 01 / {new Date().getFullYear()}</span>
          </div>}
        </section>
        <Metrics stand={p.stand}/>
        {p.catalogCategories}
        <Stock
          count={p.vehicleCount}
          cards={p.vehicleCards}
          cardSize="large"
          label="A coleção"
        />
        <ReferenceCommercialFlow stand={p.stand}/>
      </main>
      <ThemeFooter stand={p.stand} />
    </>
  );
}
function Circuit(p: Props) {
  const media=previewHero(p);
  return (
    <>
      <header className="circuit-header">
        <a href={`/stand/${p.stand.slug}`}>
          <ThemeBrand stand={p.stand} />
        </a>
        <ThemeNav stand={p.stand} />
        <div className="theme-header-actions"><span>{String(p.vehicleCount).padStart(2, "0")} EM STOCK</span>{p.visitorTools}</div>
      </header>
      <main>
        <section className={`circuit-hero ${media?"has-media":"without-media"}`}>
          <Hero
            stand={p.stand}
            media={media}
            kicker="Performance com confiança"
          />
          <aside className="circuit-search" aria-label="Encontrar uma viatura">
            <strong>Encontre a sua viatura</strong>
            {p.catalogControls ?? <span>{p.vehicleCount} {p.vehicleCount===1?"viatura disponível":"viaturas disponíveis"}</span>}
          </aside>
          <b className="circuit-index">01</b>
        </section>
        <div className="circuit-ticker">
          PERFORMANCE · PRECISÃO · CONFIANÇA · PERFORMANCE · PRECISÃO ·
          CONFIANÇA
        </div>
        <Metrics stand={p.stand}/>
        {p.catalogCategories}
        <Stock
          count={p.vehicleCount}
          cards={p.vehicleCards}
          cardSize="large"
          label="Viaturas em destaque"
        />
        <ReferenceCommercialFlow stand={p.stand}/>
      </main>
      <ThemeFooter stand={p.stand} />
    </>
  );
}
function Horizonte(p: Props) {
  const media=previewHero(p);
  const directPhone=p.stand.profile?.direct_phone||p.stand.phone;
  return (
    <div className="horizonte-shell">
      <aside className="horizonte-sidebar" aria-label="Navegação do site">
        <a href={`/stand/${p.stand.slug}`}>
          <ThemeBrand stand={p.stand} />
        </a>
        <ThemeNav stand={p.stand} />
        {directPhone&&<a className="horizonte-direct-phone" href={`tel:${directPhone}`}><small>Linha direta</small><strong>{directPhone}</strong></a>}
      </aside>
      <div className="horizonte-page">
       <main>
        <section className={`horizonte-hero ${media?"has-media":"without-media"}`}>
          <div>
            <Hero stand={p.stand} kicker="Feito para o seu caminho" />
          </div>
          {media&&<div className="horizonte-media">
            {media}
            {p.vehicleCount>0&&<span>{p.vehicleCount} {p.vehicleCount===1?"oportunidade":"oportunidades"} para descobrir</span>}
          </div>}
        </section>
        <Metrics stand={p.stand}/>
        <section className="horizonte-search" aria-label="Encontrar uma viatura">
          <strong>Encontre a sua próxima viatura</strong>
          {p.catalogControls ?? <span>{p.vehicleCount} {p.vehicleCount===1?"viatura disponível":"viaturas disponíveis"}</span>}
        </section>
        {p.catalogCategories}
        <Stock
          count={p.vehicleCount}
          cards={p.vehicleCards}
          cardSize="mosaic"
          label="Viaturas em destaque"
        />
        <ReferenceCommercialFlow stand={p.stand}/>
       </main>
       <ThemeFooter stand={p.stand} />
      </div>
    </div>
  );
}
function Galeria(p: Props) {
  const media=previewHero(p);
  const founded=p.stand.profile?.founded_year;
  const reputation=p.stand.reputation;
  const hasRating=reputation?.average_rating!=null&&reputation?.review_count!=null;
  const sideMetrics=(p.stand.metrics??[]).filter(item=>(!founded||item.key!=="years_active")&&(!hasRating||item.key!=="satisfaction")).slice(0,2);
  const showSidePanel=Boolean(founded||hasRating||sideMetrics.length);
  const canPublish=(p.stand.services??[]).some(item=>item.key==="vehicle_listing"&&item.active!==false);
  const metricValue=(item:TemplateMetric)=>item.format==="rating"?`${new Intl.NumberFormat("pt-PT",{maximumFractionDigits:1}).format(item.value)}/5`:item.format==="years"?`${item.value} ${item.value===1?"ano":"anos"}`:item.format==="months"?`${item.value} ${item.value===1?"mês":"meses"}`:new Intl.NumberFormat("pt-PT").format(item.value);
  return (
    <>
      <header className="galeria-header">
        <a href={`/stand/${p.stand.slug}`}>
          <ThemeBrand stand={p.stand} />
        </a>
        <ThemeNav stand={p.stand} />
        <div className="theme-header-actions">{canPublish&&<a href="#vender">Publicar viatura</a>}{p.visitorTools}</div>
      </header>
      <main>
        <section className={`galeria-showroom${showSidePanel?" has-side-panel":" without-side-panel"}`}>
          {showSidePanel&&<aside className="galeria-reputation-panel" aria-label="Indicadores e reputação do stand">
            {founded&&<article><small>CONFIANÇA</small><strong>Desde {founded}</strong></article>}
            {sideMetrics.map(item=><article key={item.key}><small>{item.label}</small><strong>{metricValue(item)}</strong><span>{item.source_label}</span></article>)}
            {hasRating&&<article className="galeria-rating"><small>AVALIAÇÕES</small><strong>{new Intl.NumberFormat("pt-PT",{maximumFractionDigits:1}).format(Number(reputation!.average_rating))}/5</strong><span>{new Intl.NumberFormat("pt-PT").format(Number(reputation!.review_count))} opiniões</span></article>}
          </aside>}
          <div className="galeria-stage">
            <section className={`galeria-hero ${media?"has-media":"without-media"}`}>
              <Hero stand={p.stand} kicker="Escolha com confiança" />
              {media&&<div className="galeria-hero-media">{media}</div>}
            </section>
            <section className="galeria-search" aria-label="Encontrar uma viatura">
              <strong>Encontre a sua viatura ideal</strong>
              {p.catalogControls??<span>{p.vehicleCount} {p.vehicleCount===1?"viatura disponível":"viaturas disponíveis"}</span>}
            </section>
          </div>
        </section>
        <div className="galeria-categories">{p.catalogCategories}</div>
        <Stock
          count={p.vehicleCount}
          cards={p.vehicleCards}
          cardSize="mosaic"
          label="Viaturas selecionadas"
        />
        <Metrics stand={p.stand}/>
        <ReferenceCommercialFlow stand={p.stand}/>
      </main>
      <ThemeFooter stand={p.stand} />
    </>
  );
}
function Mercado(p: Props) {
  const media=previewHero(p);
  const headquarters=p.stand.branches?.find(branch=>branch.is_headquarters)??p.stand.branches?.[0];
  const phone=headquarters?.phone||p.stand.phone;
  const email=headquarters?.email||p.stand.email;
  const socialLinks=Object.entries(p.stand.profile?.social_links??{}).map(([network,url])=>[network,safeTemplateHref(url)] as const).filter((entry):entry is readonly [string,string]=>Boolean(entry[1]));
  const showTopbar=Boolean(phone||email||socialLinks.length);
  return (
    <>
      {showTopbar&&<aside className="mercado-topbar" aria-label="Contactos rápidos do stand">
        <div>{phone&&<a href={`tel:${phone}`}>{phone}</a>}{email&&<a href={`mailto:${email}`}>{email}</a>}</div>
        {socialLinks.length>0&&<nav aria-label="Redes sociais">{socialLinks.map(([network,url])=><a key={network} href={url} target="_blank" rel="noopener noreferrer">{socialLabels[network]??network}</a>)}</nav>}
      </aside>}
      <header className="mercado-header">
        <a href={`/stand/${p.stand.slug}`}>
          <ThemeBrand stand={p.stand} />
        </a>
        <ThemeNav stand={p.stand} />
        <div className="theme-header-actions"><a href="#contactos">OLÁ!</a>{p.visitorTools}</div>
      </header>
      <main>
        <section className={`mercado-hero${media?" has-media":" without-media"}`}>
          <div className="mercado-hero-copy"><Hero stand={p.stand} kicker="A escolha certa, com confiança" /></div>
          {media&&<div className="mercado-hero-media">{media}<span>{p.vehicleCount} {p.vehicleCount===1?"viatura disponível":"viaturas disponíveis"}</span></div>}
          <section className="mercado-search" aria-label="Encontrar uma viatura">
            <strong>Encontre a sua viatura ideal</strong>
            {p.catalogControls??<span>{p.vehicleCount} {p.vehicleCount===1?"viatura disponível":"viaturas disponíveis"}</span>}
          </section>
        </section>
        <div className="mercado-categories">{p.catalogCategories}</div>
        <Stock
          count={p.vehicleCount}
          cards={p.vehicleCards}
          cardSize="mosaic"
          label="Viaturas em destaque"
        />
        <Metrics stand={p.stand}/>
        <ReferenceCommercialFlow stand={p.stand}/>
      </main>
      <ThemeFooter stand={p.stand} />
    </>
  );
}
export function StandHomeTemplate(props: Props) {
  if (props.layout === "atelier") return <Atelier {...props} />;
  if (props.layout === "circuit") return <Circuit {...props} />;
  if (props.layout === "horizonte") return <Horizonte {...props} />;
  if (props.layout === "galeria") return <Galeria {...props} />;
  if (props.layout === "mercado") return <Mercado {...props} />;
  return <Avenida {...props} />;
}
